import React, { useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Survey } from '@questlabs/react-sdk';
import SafeIcon from '../common/SafeIcon';

const CANDIDATES_SCORES = [
  { id: 1, name: 'Yaw Mensah', total: 92, technical: 95, experience: 88, soft: 94, culture: 90 },
  { id: 2, name: 'Ama Serwaa', total: 88, technical: 82, experience: 92, soft: 85, culture: 95 },
  { id: 3, name: 'Ekow Thompson', total: 75, technical: 88, experience: 65, soft: 70, culture: 78 },
  { id: 4, name: 'Linda Ofori', total: 84, technical: 78, experience: 85, soft: 92, culture: 82 },
];

const ScoreInsights = () => {
  const [selectedCandidate, setSelectedCandidate] = useState(CANDIDATES_SCORES[0]);
  const [showAssessment, setShowAssessment] = useState(false);

  const radarOption = {
    radar: {
      indicator: [
        { name: 'Technical', max: 100 },
        { name: 'Experience', max: 100 },
        { name: 'Soft Skills', max: 100 },
        { name: 'Culture', max: 100 },
      ],
      shape: 'circle',
      splitNumber: 4,
      axisName: { color: '#6b7280', fontSize: 12 },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
      splitArea: { show: false },
      axisLine: { lineStyle: { color: '#f1f5f9' } }
    },
    series: [{
      type: 'radar',
      data: [{
        value: [
          selectedCandidate.technical,
          selectedCandidate.experience,
          selectedCandidate.soft,
          selectedCandidate.culture
        ],
        name: selectedCandidate.name,
        symbol: 'none',
        areaStyle: { color: 'rgba(0, 174, 239, 0.2)' },
        lineStyle: { color: '#00AEEF', width: 2 },
        itemStyle: { color: '#00AEEF' }
      }]
    }]
  };

  const styleConfig = {
    Form: {
      backgroundColor: '#FFFFFF',
      borderRadius: '8px',
      padding: '24px',
      border: '1px solid #f1f5f9'
    },
    Heading: { color: '#11469A', fontSize: '18px', fontWeight: '700' },
    Description: { color: '#6b7280', fontSize: '13px', marginBottom: '20px' },
    PrimaryButton: {
      backgroundColor: '#00AEEF',
      color: '#FFFFFF',
      borderRadius: '4px',
      fontWeight: '700',
      fontSize: '12px',
      textTransform: 'uppercase'
    },
    SingleChoice: {
      style: { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '10px' },
      selectedStyle: { backgroundColor: '#00AEEF', color: '#FFFFFF' },
      hoverBackground: '#f1f5f9'
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Candidate Score Insights</h1>
          <p className="text-gray-500">In-depth performance analysis and match scoring.</p>
        </div>
        <button 
          onClick={() => setShowAssessment(!showAssessment)}
          className="btn-primary flex items-center gap-2"
        >
          <SafeIcon name={showAssessment ? "ChevronLeft" : "Edit3"} size={18} />
          {showAssessment ? "Back to Insights" : "Internal Assessment"}
        </button>
      </div>

      {showAssessment ? (
        <div className="max-w-3xl mx-auto">
          <Survey 
            styleConfig={styleConfig}
            questId="internal-assessment-quest"
            userId="admin-hr"
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Candidate List */}
          <div className="lg:col-span-1 space-y-4">
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest px-2">Select Candidate</h3>
            <div className="space-y-2">
              {CANDIDATES_SCORES.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelectedCandidate(c)}
                  className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
                    selectedCandidate.id === c.id 
                      ? 'bg-white border-[#00AEEF] shadow-md ring-1 ring-[#00AEEF]' 
                      : 'bg-white border-gray-100 hover:border-gray-300'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span className={`font-bold ${selectedCandidate.id === c.id ? 'text-[#11469A]' : 'text-gray-700'}`}>
                      {c.name}
                    </span>
                    <span className="text-xs font-bold text-[#00AEEF] bg-[#00AEEF]/10 px-2 py-0.5 rounded">
                      {c.total}% Match
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Insights Panel */}
          <div className="lg:col-span-2 space-y-6">
            <div className="card p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">{selectedCandidate.name}</h3>
                <p className="text-sm text-gray-500 mb-6 font-medium">Score Breakdown by Category</p>
                <div className="h-[300px]">
                  <ReactECharts option={radarOption} style={{ height: '100%' }} />
                </div>
              </div>
              <div className="space-y-6 flex flex-col justify-center">
                <div className="space-y-4">
                  {[
                    { label: 'Technical Proficiency', value: selectedCandidate.technical, color: 'bg-[#11469A]' },
                    { label: 'Relevant Experience', value: selectedCandidate.experience, color: 'bg-[#00AEEF]' },
                    { label: 'Communications & Soft Skills', value: selectedCandidate.soft, color: 'bg-green-500' },
                    { label: 'Culture & Value Alignment', value: selectedCandidate.culture, color: 'bg-purple-500' },
                  ].map((stat, i) => (
                    <div key={i} className="space-y-1.5">
                      <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-gray-400">
                        <span>{stat.label}</span>
                        <span className="text-gray-700">{stat.value}%</span>
                      </div>
                      <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${stat.color} transition-all duration-500`} 
                          style={{ width: `${stat.value}%` }} 
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="card p-5 bg-blue-50 border-none">
                <div className="flex items-center gap-3 mb-3 text-[#11469A]">
                  <SafeIcon name="CheckCircle" size={20} />
                  <h4 className="font-bold text-sm">Key Strength</h4>
                </div>
                <p className="text-xs text-blue-700 leading-relaxed font-medium">
                  {selectedCandidate.technical > 90 ? 'Exceptional technical depth across required stack.' : 'Strong alignment with core company values and cultural dynamics.'}
                </p>
              </div>
              <div className="card p-5 bg-orange-50 border-none">
                <div className="flex items-center gap-3 mb-3 text-orange-600">
                  <SafeIcon name="AlertCircle" size={20} />
                  <h4 className="font-bold text-sm">Improvement Area</h4>
                </div>
                <p className="text-xs text-orange-700 leading-relaxed font-medium">
                  {selectedCandidate.experience < 80 ? 'Could benefit from more hands-on experience in leadership roles.' : 'Slight gap in specialized niche tooling knowledge.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScoreInsights;